import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Send, Loader2, CheckCircle2 } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const areasOfInterest = [
  "3-Hour AI Essentials Session",
  "Full-Day Practical AI Workshop",
  "Team Training",
  "Business Workshop",
  "General Enquiry",
];

export const ContactForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    organisation: "",
    area_of_interest: "",
    message: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value) => {
    setFormData((prev) => ({ ...prev, area_of_interest: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.area_of_interest || !formData.message) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      setIsSuccess(true);
      toast.success("Thank you for your enquiry! We'll be in touch soon.");
      setFormData({
        name: "",
        email: "",
        organisation: "",
        area_of_interest: "",
        message: "",
      });
      
      // Reset success state after 5 seconds
      setTimeout(() => setIsSuccess(false), 5000);
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Something went wrong. Please try again or email us directly at info@beaiready.com.au");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="text-center py-8" data-testid="contact-form-success">
        <div className="w-16 h-16 bg-[#F0FDFA] rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle2 className="text-[#008A90]" size={32} />
        </div>
        <h3 className="text-xl font-bold text-[#0A192F] mb-2 font-['Manrope']">
          Thank you for your enquiry!
        </h3>
        <p className="text-body">
          We'll be in touch soon to discuss your training needs.
        </p>
      </div>
    );
  }

  return (
    <form 
      onSubmit={handleSubmit} 
      className="space-y-5"
      data-testid="contact-form"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <Label htmlFor="name" className="form-label">
            Name <span className="text-red-500">*</span>
          </Label>
          <Input
            id="name"
            name="name"
            type="text"
            value={formData.name}
            onChange={handleChange}
            placeholder="Your name"
            className="form-input"
            required
            data-testid="contact-form-name"
          />
        </div>
        <div>
          <Label htmlFor="email" className="form-label">
            Email <span className="text-red-500">*</span>
          </Label>
          <Input
            id="email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="your.email@example.com"
            className="form-input"
            required
            data-testid="contact-form-email"
          />
        </div>
      </div>

      <div>
        <Label htmlFor="organisation" className="form-label">
          Organisation <span className="text-[#64748B] text-sm font-normal">(optional)</span>
        </Label>
        <Input
          id="organisation"
          name="organisation"
          type="text"
          value={formData.organisation}
          onChange={handleChange}
          placeholder="Your company or organisation"
          className="form-input"
          data-testid="contact-form-organisation"
        />
      </div>

      <div>
        <Label htmlFor="area_of_interest" className="form-label">
          Area of Interest <span className="text-red-500">*</span>
        </Label>
        <Select
          value={formData.area_of_interest}
          onValueChange={handleSelectChange}
          required
        >
          <SelectTrigger 
            className="form-input h-auto"
            data-testid="contact-form-area-of-interest"
          >
            <SelectValue placeholder="Select your area of interest" />
          </SelectTrigger>
          <SelectContent>
            {areasOfInterest.map((area) => (
              <SelectItem key={area} value={area} className="py-2.5">
                {area}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label htmlFor="message" className="form-label">
          Message <span className="text-red-500">*</span>
        </Label>
        <Textarea
          id="message"
          name="message"
          value={formData.message}
          onChange={handleChange}
          placeholder="Tell us about your training needs, team size, or any questions you have..."
          className="form-input min-h-[140px] resize-none"
          required
          data-testid="contact-form-message"
        />
      </div>

      <Button
        type="submit"
        disabled={isSubmitting}
        className="w-full bg-[#0A192F] hover:bg-[#112240] text-white font-semibold px-8 py-5 text-[15px] transition-all hover:-translate-y-0.5 disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none"
        data-testid="contact-form-submit"
      >
        {isSubmitting ? (
          <>
            <Loader2 className="animate-spin" size={18} />
            Sending...
          </>
        ) : (
          <>
            <Send size={18} />
            Send Enquiry
          </>
        )}
      </Button>
      
      <p className="text-center text-sm text-[#64748B]">
        Or email us directly at{" "}
        <a href="mailto:info@beaiready.com.au" className="text-[#008A90] hover:underline">
          info@beaiready.com.au
        </a>
      </p>
    </form>
  );
};

export default ContactForm;
